/**
 * API client.
 *
 * Auth rides on an HttpOnly session cookie, so nothing here stores or handles a
 * token. The one thing the client must do is echo the CSRF cookie back in a
 * header on state-changing requests -- that pairing is what proves the request
 * came from same-origin script rather than a cross-site form post.
 */

const CSRF_COOKIE = 'chronos_csrf';
const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Server asked for a second factor rather than rejecting the password. */
    readonly mfaRequired = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export async function api<T>(
  path: string,
  options: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...init } = options;
  const method = (init.method ?? 'GET').toUpperCase();

  const headers = new Headers(init.headers);
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (UNSAFE.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }

  const res = await fetch(path, {
    ...init,
    method,
    headers,
    // Cookies are the credential; without this the session never travels.
    credentials: 'same-origin',
    body: json !== undefined ? JSON.stringify(json) : init.body,
  });

  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, detail, res.headers.get('X-MFA-Required') === '1');
  }

  return body as T;
}
